import React, { useState } from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { CheckIcon, CoinIcon, CompanionIcon } from '../shared/RelicVaultIcons';
import { COLORS, bodyFont, em, headingFont, hoverTransition } from '../shared/theme';
import { companionVisuals } from 'utils/relicVault/colorUtils';
import { IS_WEB, webOnly } from 'utils/relicVault/webStyle';

import { TYPE } from 'style/typography';

const ART_HEIGHT = 212;
// The design sizes the art to 124% of its box, anchored right 18 / top 10.
const IMAGE_HEIGHT = ART_HEIGHT * 1.24;

/**
 * A guardian companion card (.companion-card).
 *
 * Added to the handoff: an owned companion's button equips it ("Equip"), and
 * the one currently following the avatar shows "Equipped". The handoff only
 * had purchase, with no way to choose between owned companions.
 */
const CompanionCard = ({
  companion,
  owned,
  equipped,
  canAfford,
  balance,
  width,
  onSelect,
  onEquip,
}) => {
  const [hovered, setHovered] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const visuals = companionVisuals(companion.colorHex);
  const shortfall = Math.max(0, companion.price - (balance ?? 0));
  const needText = !owned && !canAfford ? `Need ${shortfall} more` : '';
  const showNeed = IS_WEB && buttonHovered && !!needText;
  const aspect =
    companion.artWidth && companion.artHeight
      ? companion.artWidth / companion.artHeight
      : 0.7;

  const onPressButton = () => {
    if (owned) {
      if (!equipped) onEquip(companion.guildId);
      return;
    }
    onSelect(companion.guildId);
  };

  return (
    <TouchableOpacity
      activeOpacity={1}
      onPress={() => onSelect(companion.guildId)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityLabel={`${companion.name}, ${companion.guildTitle}`}
      style={[
        styles.card,
        { width, borderColor: `${companion.colorHex}70`, backgroundColor: visuals.dark },
        webOnly({ backgroundImage: visuals.cardWash, boxShadow: visuals.borderGlow }),
        hovered && styles.cardLifted,
        hoverTransition(),
      ]}>
      <View style={styles.art}>
        <Image
          source={{ uri: companion.artUrl }}
          resizeMode="contain"
          style={[styles.artImage, { width: IMAGE_HEIGHT * aspect }]}
        />
        <View
          pointerEvents="none"
          style={[
            styles.glow,
            IS_WEB
              ? {
                  backgroundImage: `radial-gradient(circle, ${companion.colorHex}75 0%, ${companion.colorHex}00 72%)`,
                  filter: 'blur(22px)',
                }
              : { backgroundColor: `${companion.colorHex}30` },
          ]}
        />
        <View
          style={[
            styles.badge,
            { borderColor: companion.colorHex, backgroundColor: visuals.dark },
            webOnly({
              backgroundImage: visuals.badgeGlow,
              boxShadow: '0 4px 14px rgba(0,0,0,0.45)',
            }),
          ]}>
          <CompanionIcon kind={companion.iconKind} size={24} color={companion.colorHex} />
        </View>
        <View style={styles.body}>
          <Text style={styles.name}>{companion.name}</Text>
          <Text style={styles.traits}>{(companion.traits || []).join(' • ')}</Text>
        </View>
      </View>

      <View
        style={[
          styles.footer,
          { backgroundColor: companion.colorHex },
          webOnly({
            backgroundImage: visuals.guildGradient,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
          }),
        ]}>
        <Text style={styles.guildLabel}>{companion.guildTitle}</Text>
        <TouchableOpacity
          activeOpacity={0.9}
          disabled={equipped || (!owned && !canAfford)}
          onPress={onPressButton}
          onMouseEnter={() => setButtonHovered(true)}
          onMouseLeave={() => setButtonHovered(false)}
          accessibilityRole="button"
          accessibilityLabel={
            equipped
              ? `${companion.name} is equipped`
              : owned
              ? `Equip ${companion.name}`
              : `Unlock ${companion.name} for ${companion.price}`
          }
          style={[
            styles.buyBtn,
            equipped
              ? styles.buyBtnEquipped
              : [
                  { backgroundColor: visuals.light },
                  webOnly({
                    backgroundImage: visuals.buttonGradient,
                    boxShadow:
                      '0 6px 16px rgba(0,0,0,0.4), 0 0 16px rgba(255,255,255,0.18), inset 0 1px 0 rgba(255,255,255,0.45)',
                  }),
                ],
            !owned && !canAfford && !showNeed && styles.buyBtnDisabled,
            showNeed && webOnly({ filter: 'saturate(0.35) brightness(0.68)' }),
            buttonHovered && !equipped && !needText && styles.buyBtnHover,
            hoverTransition(),
          ]}>
          {showNeed ? (
            <Text style={styles.needText} numberOfLines={1}>
              {needText}
            </Text>
          ) : equipped ? (
            <>
              <CheckIcon size={12} color={COLORS.green} />
              <Text style={[styles.btnLabel, styles.btnLabelEquipped]}>Equipped</Text>
            </>
          ) : owned ? (
            <Text style={[styles.btnLabel, styles.btnLabelAlone]}>Equip</Text>
          ) : (
            <>
              <CoinIcon size={15} light />
              <Text style={styles.btnPrice}>{companion.price}</Text>
              <Text style={styles.btnLabel}>Unlock</Text>
            </>
          )}
        </TouchableOpacity>
      </View>

      {owned && (
        <View
          style={[
            styles.ownedBadge,
            webOnly({
              backgroundImage: `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldDeep})`,
              boxShadow: '0 3px 10px rgba(240,169,58,0.45)',
            }),
          ]}>
          <Text style={styles.ownedBadgeText}>{'✓'} Already a Companion</Text>
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    position: 'relative',
    borderWidth: 1.5,
    borderRadius: 18,
  },
  cardLifted: {
    transform: [{ translateY: -3 }],
  },
  ownedBadge: {
    position: 'absolute',
    top: -9,
    right: 12,
    zIndex: 10,
    backgroundColor: COLORS.goldDeep,
    paddingVertical: 4,
    paddingHorizontal: 9,
    borderRadius: 999,
  },
  ownedBadgeText: {
    ...headingFont('800'),
    fontSize: TYPE.xs,
    color: '#2B1E00',
  },
  art: {
    position: 'relative',
    zIndex: 1,
    height: ART_HEIGHT,
    borderTopLeftRadius: 18,
    borderTopRightRadius: 18,
  },
  artImage: {
    position: 'absolute',
    right: 18,
    top: 10,
    height: IMAGE_HEIGHT,
    zIndex: 1,
  },
  glow: {
    position: 'absolute',
    right: -10,
    top: '12%',
    width: '78%',
    height: '78%',
    borderRadius: 999,
    zIndex: 0,
  },
  badge: {
    position: 'absolute',
    top: 12,
    left: 12,
    width: 52,
    height: 52,
    borderRadius: 26,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
  },
  body: {
    position: 'absolute',
    left: 16,
    bottom: 12,
    width: '58%',
    zIndex: 2,
  },
  name: {
    ...headingFont('800'),
    fontSize: TYPE['3xl'],
    lineHeight: 38,
    letterSpacing: em(-0.01, 27),
    color: '#F5F1FF',
    textShadowColor: 'rgba(0,0,0,0.65)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 10,
  },
  traits: {
    ...bodyFont('600'),
    fontSize: TYPE.sm,
    lineHeight: 17.5,
    marginTop: 5,
    color: '#CFC5FF',
    textShadowColor: 'rgba(0,0,0,0.65)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 6,
  },
  footer: {
    position: 'relative',
    zIndex: 4,
    marginTop: 8,
    paddingTop: 10,
    paddingHorizontal: 16,
    paddingBottom: 16,
    borderBottomLeftRadius: 16.5,
    borderBottomRightRadius: 16.5,
    alignItems: 'center',
  },
  guildLabel: {
    ...headingFont('800'),
    fontSize: TYPE.sm,
    letterSpacing: em(0.07, 13),
    textTransform: 'uppercase',
    textAlign: 'center',
    color: '#FFFFFF',
    textShadowColor: 'rgba(0,0,0,0.35)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 3,
  },
  buyBtn: {
    width: '66%',
    minHeight: 42,
    marginTop: 8,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.5)',
    borderRadius: 999,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  buyBtnHover: {
    transform: [{ translateY: -1 }],
  },
  buyBtnDisabled: {
    opacity: 0.7,
  },
  buyBtnEquipped: {
    backgroundColor: 'rgba(74,222,128,0.16)',
    borderColor: 'rgba(74,222,128,0.4)',
  },
  btnPrice: {
    ...headingFont('800'),
    fontSize: TYPE.sm,
    letterSpacing: em(0.03, 13),
    color: '#FFFFFF',
    marginLeft: 6,
  },
  btnLabel: {
    ...headingFont('800'),
    fontSize: TYPE.sm,
    letterSpacing: em(0.03, 13),
    textTransform: 'uppercase',
    color: '#FFFFFF',
    marginLeft: 6,
  },
  btnLabelAlone: {
    marginLeft: 0,
  },
  btnLabelEquipped: {
    color: COLORS.green,
  },
  needText: {
    ...headingFont('800'),
    fontSize: TYPE.xs,
    letterSpacing: em(0.02, 11.5),
    textTransform: 'uppercase',
    color: '#FFFFFF',
  },
});

export default React.memo(CompanionCard);
